import { COLLISION_CELLS_PER_TILE } from "../document/types.ts";
import { reachWindow, type ThingBox } from "./cell-sources.ts";
import { fieldsOfThing, setThingField, type FieldEditHost, type PickOwner } from "./field-edits.ts";
import { schemaOfThing, selectedAlone, sameThing, type FieldOwnerHost } from "./field-owner.ts";
import { addPathPoint, movePathPoint, pathCellOfGrid, pathPointAt, pathPointsOf, pathValue, pathWindowAllows, removePathPoint, type PathPoint } from "./path-fields.ts";
import type { ReferenceThing } from "./references.ts";
import type { PropertySchema } from "./schema.ts";

/**
 * Edit on map for a path field (ADR-136 decision 2): which path of which thing the map edits, view state never
 * saved or undone, and the edits it writes, each one command on that thing. It runs for one thing at a time, the
 * lone selected prop or marker, on a layer the tools may edit, and is let go once any of that stops holding. A
 * press on empty ground adds a point at the path's end; a press on a point drags it, shown moving and written as
 * one command when it is dropped; a point is removed as one command. Points keep to the thing's reach window, so
 * a path never leaves what the editor draws. Nothing here names a game: a path is a list of cells.
 */

/**
 * What the edits read from the store.
 */
export interface PathFieldHost extends FieldEditHost, FieldOwnerHost {
  readonly box: (owner: ReferenceThing) => ThingBox | null;
  readonly isEditable: (layerId: string) => boolean;
  readonly notify: () => void;
  readonly endOthers: () => void;
}

/**
 * The path the map edits: whose, which field and its title.
 */
export interface PathFieldEditing {
  readonly owner: ReferenceThing;
  readonly field: string;
  readonly title: string;
}

/**
 * One path a thing holds, for the map to draw and the inspector to list.
 */
export interface PathView {
  readonly field: string;
  readonly title: string;
  readonly points: readonly PathPoint[];
  readonly closed: boolean;
  readonly max: number;
}

/**
 * The most points a path holds when its schema sets no limit.
 */
export const DEFAULT_PATH_POINTS = 64;

/**
 * A point being dragged: its place in the order and the cell it is shown on.
 */
interface PathDrag {
  readonly index: number;
  readonly cell: PathPoint;
}

/**
 * The path fields' Edit on map.
 */
export class PathFieldEdits {
  private edited: PathFieldEditing | null = null;
  private drag: PathDrag | null = null;

  /**
   * @param host The store's side.
   */
  public constructor(private readonly host: PathFieldHost) {}

  /**
   * The path the map edits.
   *
   * @returns It, or null while none is edited.
   */
  public get editing(): PathFieldEditing | null {
    return this.edited;
  }

  /**
   * The point being dragged and the cell it is shown on.
   *
   * @returns The drag, or null while none runs.
   */
  public get dragging(): { readonly index: number; readonly cell: PathPoint } | null {
    return this.drag;
  }

  /**
   * Whether the map edits a path of a thing.
   *
   * @param owner The thing.
   * @param field The path field's name.
   * @returns True while it does.
   */
  public isEditing(owner: ReferenceThing, field: string): boolean {
    return this.edited !== null && sameThing(this.edited.owner, owner) && this.edited.field === field;
  }

  /**
   * Starts editing a thing's path on the map, ending activation's and the cells fields' Edit on map.
   *
   * @param owner The thing, which must be selected alone.
   * @param field A path field of its type.
   * @returns True when editing started, false when it was refused.
   */
  public startEditing(owner: ReferenceThing, field: string): boolean {
    const view = this.editableView(owner, field);
    if (view === undefined) {
      return false;
    }
    if (!this.isEditing(owner, field)) {
      this.host.endOthers();
      this.drag = null;
      this.edited = { owner, field, title: view.title };
      this.host.notify();
    }
    return true;
  }

  /**
   * Ends editing, notifying only when it was under way.
   */
  public endEditing(): void {
    if (this.edited !== null) {
      this.edited = null;
      this.drag = null;
      this.host.notify();
    }
  }

  /**
   * Lets editing go once its path can no longer be edited, without notifying: the store calls it on the way to
   * notifying its subscribers.
   */
  public release(): void {
    if (this.edited !== null && this.editableView(this.edited.owner, this.edited.field) === undefined) {
      this.edited = null;
      this.drag = null;
    }
  }

  /**
   * Every path a thing's fields hold, in the schema's order, for the map to draw: a point being dragged is shown
   * on the cell it is dragged to.
   *
   * @param owner The thing.
   * @returns The paths, none for a thing gone or of a type the game does not register.
   */
  public pathsOf(owner: ReferenceThing): readonly PathView[] {
    return this.viewsOf(owner, this.drag);
  }

  /**
   * A thing's paths with one point shown on another cell, or as stored.
   *
   * @param owner The thing.
   * @param dragged The drag to show, or null for the points as stored.
   * @returns The paths.
   */
  private viewsOf(owner: ReferenceThing, dragged: PathDrag | null): readonly PathView[] {
    const schema = schemaOfThing(this.host, owner);
    const fields = fieldsOfThing(this.host.map(), owner) ?? {};
    if (schema === undefined) {
      return [];
    }
    const views: PathView[] = [];
    for (const [field, property] of Object.entries(schema.properties)) {
      if (property.type !== "path") {
        continue;
      }
      let points = pathPointsOf(fields[field]);
      if (dragged !== null && this.isEditing(owner, field)) {
        points = points.map((point, at) => (at === dragged.index ? dragged.cell : point));
      }
      views.push({ field, title: property.title ?? field, points, closed: closedOf(schema.properties, property, fields), max: property.maxItems ?? DEFAULT_PATH_POINTS });
    }
    return views;
  }

  /**
   * The point on a collision grid cell in the path being edited.
   *
   * @param x The grid cell's column.
   * @param y The grid cell's row.
   * @returns The point's index, or -1 when none is there or nothing is edited.
   */
  public pointAt(x: number, y: number): number {
    const target = this.target();
    return target === null ? -1 : pathPointAt(target.points, pathCellOfGrid(target.box, x, y));
  }

  /**
   * Adds a point at the end of the path being edited, as one command.
   *
   * @param x The grid cell's column.
   * @param y The grid cell's row.
   * @returns False when nothing is edited, the cell is out of reach, holds a point already, or the path is full.
   */
  public addAt(x: number, y: number): boolean {
    const target = this.target();
    if (target === null) {
      return false;
    }
    const cell = pathCellOfGrid(target.box, x, y);
    const next = pathWindowAllows(target.box, target.window, cell) ? addPathPoint(target.points, cell, target.max) : null;
    return next !== null && this.write(next);
  }

  /**
   * Starts dragging a point of the path being edited.
   *
   * @param index The point's index.
   * @returns False when nothing is edited or the path has no such point.
   */
  public beginDrag(index: number): boolean {
    const target = this.target();
    const point = target?.points[index];
    if (target === null || point === undefined) {
      return false;
    }
    this.drag = { index, cell: point };
    this.host.notify();
    return true;
  }

  /**
   * Shows the dragged point on another cell; a cell out of reach or holding another point leaves it where it was
   * shown last.
   *
   * @param x The grid cell's column.
   * @param y The grid cell's row.
   * @returns True when the point moved on screen.
   */
  public dragTo(x: number, y: number): boolean {
    const target = this.target();
    const drag = this.drag;
    if (target === null || drag === null) {
      return false;
    }
    const cell = pathCellOfGrid(target.box, x, y);
    const same = cell[0] === drag.cell[0] && cell[1] === drag.cell[1];
    const taken = target.points.some((point, at) => at !== drag.index && point[0] === cell[0] && point[1] === cell[1]);
    if (same || taken || !pathWindowAllows(target.box, target.window, cell)) {
      return false;
    }
    this.drag = { index: drag.index, cell };
    this.host.notify();
    return true;
  }

  /**
   * Drops the dragged point where it is shown, as one command.
   *
   * @returns False when nothing was dragged, the point is back where it began, or the command was refused.
   */
  public drop(): boolean {
    const target = this.target();
    const drag = this.drag;
    this.drag = null;
    if (target === null || drag === null) {
      this.host.notify();
      return false;
    }
    const next = movePathPoint(target.points, drag.index, drag.cell);
    const written = next !== null && this.write(next);
    if (!written) {
      this.host.notify();
    }
    return written;
  }

  /**
   * Puts a dragged point back where it began, writing nothing.
   */
  public cancelDrag(): void {
    if (this.drag !== null) {
      this.drag = null;
      this.host.notify();
    }
  }

  /**
   * Removes a point of the path being edited, as one command; the rest close up in order.
   *
   * @param index The point's index.
   * @returns False when nothing is edited or the path has no such point.
   */
  public removePoint(index: number): boolean {
    const target = this.target();
    const next = target === null ? null : removePathPoint(target.points, index);
    return next !== null && this.write(next);
  }

  /**
   * Empties a path as one command: the field then holds no value.
   *
   * @param owner The thing, or the selected props.
   * @param field The path field's name.
   * @returns False when nothing changed or the command was refused.
   */
  public clear(owner: PickOwner, field: string): boolean {
    return setThingField(this.host, owner, { field }, null);
  }

  /**
   * The path being edited with its thing's box, reach window and limit.
   *
   * @returns The target, or null while no path is edited or its thing is gone.
   */
  private target(): { readonly edited: PathFieldEditing; readonly points: readonly PathPoint[]; readonly box: ThingBox; readonly window: ReturnType<typeof reachWindow>; readonly max: number } | null {
    const edited = this.edited;
    const box = edited === null ? null : this.host.box(edited.owner);
    const view = edited === null ? undefined : this.editableView(edited.owner, edited.field);
    if (edited === null || box === null || view === undefined) {
      return null;
    }
    const map = this.host.map();
    return { edited, points: view.points, box, window: reachWindow(box, map.width * COLLISION_CELLS_PER_TILE, map.height * COLLISION_CELLS_PER_TILE), max: view.max };
  }

  /**
   * Writes the edited path's points.
   *
   * @param points The points after the edit.
   * @returns False when nothing changed or the command was refused.
   */
  private write(points: readonly PathPoint[]): boolean {
    const edited = this.edited;
    return edited !== null && setThingField(this.host, edited.owner, { field: edited.field }, pathValue(points));
  }

  /**
   * The path at a field, when the map may edit it: the thing selected alone, on an editable layer, the field a
   * path field of its type.
   *
   * @param owner The thing.
   * @param field The field's name.
   * @returns The path as stored (without a drag), or undefined.
   */
  private editableView(owner: ReferenceThing, field: string): PathView | undefined {
    if (!this.host.isEditable(owner.layerId) || !selectedAlone(this.host, owner)) {
      return undefined;
    }
    return this.viewsOf(owner, null).find((v) => v.field === field);
  }
}

/**
 * Whether a path is closed into a loop: the choice it names holds the option, its schema default counting for a
 * choice the thing leaves unset.
 *
 * @param properties The thing's type's properties.
 * @param path The path's property.
 * @param fields The thing's field values.
 * @returns True when the path returns to its first point.
 */
function closedOf(properties: { readonly [name: string]: PropertySchema }, path: Extract<PropertySchema, { readonly type: "path" }>, fields: { readonly [key: string]: unknown }): boolean {
  const close = path.closeWhen;
  if (close === undefined) {
    return false;
  }
  const sibling = properties[close.field];
  const fallback = sibling?.type === "string" ? sibling.default : undefined;
  return (fields[close.field] ?? fallback) === close.equals;
}
